/* eslint-disable no-shadow */
// eslint-disable-next-line import/no-cycle
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { HttpError, PMSError, ValidationError } from '@/errors';

const endpoint = `${apiEndpoint}/management/groups`;

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 400: // BadRequest
      err = new HttpError(status, data.message);
      break;
    case 409: // PMSError
      err = new PMSError(status, data.message);
      break;
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      commit('validationError', err);
      break;
    case 500:
      err = new Error(data.message);
      break;
    default:
      break;
  }
  throw err;
}

const state = {
  groups: null,
  pending: false,
  error: null,
  updatePending: false,
  updateError: null,
  validationError: null,
  group: null,
};

const getters = {
  groups: (state) => (state.groups ?? []),
  group: (state) => state.group,
};

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
    state.validationError = null;
  },
  beforeLoading(state, initial = true) {
    if (initial) {
      state.groups = null;
    }
    state.groups = null;
    state.error = null;
    state.pending = true;
  },
  update(state, error = null) {
    state.updatePending = false;
    state.updateError = error;
  },
  afterUpdateRequest(state) {
    state.updatePending = false;
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.updatePending = true;
    state.validationError = null;
  },
  groups(state, data) {
    state.pending = false;
    state.groups = data;
  },
  group(state, data) {
    state.pending = false;
    state.group = data;
  },
  error(state, error) {
    state.pending = false;
    state.updatePending = false;
    state.error = error;
  },
  validationError(state, error) {
    state.validationError = error;
  },
  updateGroupData(state, data) {
    state.group = data;
    state.updatePending = false;
    state.updateError = null;
  },
};

const actions = {
  async getGroups({ commit }, payload) {
    const forced = payload?.forced || true;
    commit('beforeLoading', forced);
    try {
      const { data } = await ApiService.post(`${endpoint}/filter`, payload);
      commit('groups', data);
      return data;
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
      return [];
    }
  },
  async updateGroup({ commit }, data) {
    commit('beforeUpdate');
    try {
      let payload;
      if (
        (data.logo == null || data.logo.upload == null)
        && (data.favicon == null || data.favicon.upload == null)
        && (data.bookingEngineLogo == null || data.bookingEngineLogo.upload == null)
        && (data.secondary_logo == null || data.secondary_logo.upload == null)
      ) {
        payload = { ...data, _method: 'put' };
      } else {
        payload = new FormData();
        payload.append('_method', 'put');
        payload.appendFromObject(data);
      }
      const { data: group } = await ApiService.post(`${endpoint}/${data.id}`, payload);
      commit('updateGroupData', group);
    } catch (error) {
      commit('update', error);
      handleApiError(commit, error);
    }
  },
  async verifyExtranetUrlDomain({ commit }, payload) {
    commit('beforeUpdate');
    try {
      await ApiService.post(`${endpoint}/verify-domain/${payload.id}`, payload);
      commit('afterUpdateRequest');
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
    }
  },
  async verifyBookingEngineUrlDomain({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const { data: group } = await ApiService.post(`${endpoint}/verify-domain/${payload.id}`, payload);
      commit('updateGroupData', group);
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
    }
  },
  async fetchData({ commit }, { id, force = false } = {}) {
    commit('beforeLoading', force);
    try {
      const { data } = await ApiService.get(`${endpoint}/${id}`);
      commit('group', { data });
      return data;
    } catch (error) {
      commit('data', { error });
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
